import { useEffect, useState } from "react";

// ── NowBar preferences ───────────────────────────────────────────────────────
// Per-user, per-device personalization of what the top-nav "NowBar" surfaces.
// This is PURE UI personalization — it only ever HIDES items the user is already
// allowed to see — so it lives in localStorage (exactly like theme / font size),
// with no server component and no security weight. SettingsPage writes it; NowBar
// reads it. Both listen for NOWBAR_PREFS_EVENT so a change applies instantly.

const STORAGE_KEY = "nowbar_prefs";
export const NOWBAR_PREFS_EVENT = "nowbar-prefs-changed";

// Three axes:
//   • sources — the kinds of items the bar rotates through.
//   • scopes  — which calendar events, by WHO can see them, are eligible.
//   • flicker — opt-in attention effect. When ON, an event marked Flicker (the
//     per-event checkbox on the calendar form, mig 180) makes the bar visibly
//     flicker while it's on screen. OFF by default: it's deliberately loud, and
//     it also gates whether the per-event checkbox is offered at all.
// `others_private` defaults OFF on purpose: owner/developer get a god-view of
// everyone's private events on the Calendar, and those buried their real
// (own/shared/public) items in the bar — which is the whole reason this exists.
// `scopes` apply to calendar events ONLY. Todos need no scope axis: they are
// strictly private (mig 181), so the only todos that exist for you are your own.
export const DEFAULT_NOWBAR_PREFS = {
  sources: { updates: true, events: true, approvals: true, notifications: true, todos: true },
  scopes: { own: true, shared: true, public: true, others_private: false },
  flicker: false,
};

const merge = (raw) => ({
  sources: { ...DEFAULT_NOWBAR_PREFS.sources, ...(raw?.sources || {}) },
  scopes: { ...DEFAULT_NOWBAR_PREFS.scopes, ...(raw?.scopes || {}) },
  flicker: raw?.flicker === true,
});

export const getNowBarPrefs = () => {
  try {
    return merge(JSON.parse(localStorage.getItem(STORAGE_KEY)));
  } catch {
    return merge(null);
  }
};

export const setNowBarPrefs = (prefs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(merge(prefs)));
  window.dispatchEvent(new Event(NOWBAR_PREFS_EVENT));
};

// Classify a calendar event (as GET /api/calendar/events returns it) into exactly
// one visibility scope, so the bar can filter by the user's chosen scopes:
//   own            — I created it (any visibility)
//   shared         — someone else's private event shared with me
//   public         — visible to everyone
//   others_private — someone else's private event I only see via the god-view
export const eventScope = (e, userId) => {
  if (e.is_mine) return "own";
  const sharedWithMe =
    !!e.shared_by ||
    (Array.isArray(e.shared_with) && userId && e.shared_with.some((u) => u && u.id === userId));
  if (sharedWithMe) return "shared";
  if (e.visibility === "public") return "public";
  return "others_private";
};

// Subscribe to NowBar prefs; re-renders on any change (this tab via the setter,
// or another tab via the native `storage` event).
export default function useNowBarPrefs() {
  const [prefs, setPrefs] = useState(getNowBarPrefs);
  useEffect(() => {
    const reload = () => setPrefs(getNowBarPrefs());
    window.addEventListener(NOWBAR_PREFS_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(NOWBAR_PREFS_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);
  return prefs;
}
